'use client'

import { Pill } from '@repo/ui/Pill'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import MUIMenuItem from '@mui/material/MenuItem'
import MUISelect, { SelectChangeEvent } from '@mui/material/Select'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'

type ClaimStatusOption = {
  value: number
  label: string
  color?: string
  backgroundColor?: string
}

type ClaimStatusDropdownButtonProps = {
  value: number | null // null means "no selection"
  options?: ClaimStatusOption[]
  onChange: (nextValue: number | null) => void // can be null to deselect
  top?: boolean
  isExpensesTable?: boolean
  hiddenStatuses?: number[]
  sx?: object
  disabledStatuses?: number[]
  disabled?: boolean
  statusTooltips?: Record<number, string>
}

const DEFAULT_STATUS_STYLES: Record<number, { color: string; backgroundColor: string }> = {
  0: { color: '#415078', backgroundColor: '#DFE0E4' }, // Draft
  1: { color: '#415078', backgroundColor: '#DFE0E4' }, // Submitted
  2: { color: '#FFFFFF', backgroundColor: '#4A7D85' }, // Approved
  3: { color: '#FFFFFF', backgroundColor: '#B43F45' }, // Rejected
  4: { color: '#FFFFFF', backgroundColor: '#CF7134' }, // ActionRequired
  5: { color: '#FFFFFF', backgroundColor: '#5772BD' }, // ReadyToPay
  6: { color: '#FFFFFF', backgroundColor: '#677392' }, // Posted
  7: { color: '#FFFFFF', backgroundColor: '#29384B' }, // Paid
  8: { color: '#FFFFFF', backgroundColor: '#29384B' } // TOIL Agreed
}

const DEFAULT_OPTIONS: ClaimStatusOption[] = [
  { value: 0, label: 'Draft' },
  { value: 1, label: 'Submitted' },
  { value: 2, label: 'Approved' },
  { value: 3, label: 'Rejected' },
  { value: 4, label: 'Action required' },
  { value: 5, label: 'Ready to pay' },
  { value: 6, label: 'Posted' },
  { value: 7, label: 'Paid' },
  { value: 8, label: 'TOIL agreed' }
]

export const ClaimStatusDropdownButton = ({
  value,
  options,
  onChange,
  top = false,
  isExpensesTable = false,
  hiddenStatuses = [],
  sx,
  disabledStatuses = [],
  disabled = false,
  statusTooltips = {}
}: ClaimStatusDropdownButtonProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [paperWidth, setPaperWidth] = useState<number | undefined>(undefined)
  const [open, setOpen] = useState(false)

  const syncPaperWidth = useCallback(() => {
    const el = anchorRef.current
    if (!el) return
    setPaperWidth(Math.round(el.getBoundingClientRect().width))
  }, [])

  useLayoutEffect(() => {
    syncPaperWidth()
  }, [syncPaperWidth])

  useEffect(() => {
    window.addEventListener('resize', syncPaperWidth)
    return () => window.removeEventListener('resize', syncPaperWidth)
  }, [syncPaperWidth])

  const baseOptions = options?.length ? options : DEFAULT_OPTIONS
  const filteredOptions = isExpensesTable
    ? baseOptions.filter((o) => o.value !== 8 && o.value !== 0)
    : hiddenStatuses.length > 0
      ? baseOptions.filter((o) => !hiddenStatuses.includes(o.value))
      : baseOptions

  const resolvedOptions: ClaimStatusOption[] = filteredOptions.map((opt) => {
    const style = DEFAULT_STATUS_STYLES[opt.value]
    return {
      ...opt,
      color: opt.color ?? style?.color ?? '#111111',
      backgroundColor: opt.backgroundColor ?? style?.backgroundColor ?? '#E5E7EB'
    }
  })

  const selectedOption = resolvedOptions.find((o) => o.value === value)
  const hasSelection = value !== null && selectedOption !== undefined

  // Always give Select a valid value: '' when nothing is selected / invalid value
  const selectValue = selectedOption ? String(selectedOption.value) : ''

  const selectedBg = selectedOption?.backgroundColor
  const selectedText = selectedOption?.color

  const handleOpen = () => {
    syncPaperWidth()
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const handleChange = (event: SelectChangeEvent<string>) => {
    // Normal selection path (different value)
    const numericValue = parseInt(event.target.value, 10)
    onChange(isNaN(numericValue) ? null : numericValue)
    setOpen(false)
  }

  return (
    <MUIStack data-testid="ClaimStatusDropdownButton" direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
      <div ref={anchorRef} style={{ width: '100%' }}>
        <MUISelect<string>
          value={selectValue}
          displayEmpty
          open={open}
          onOpen={handleOpen}
          disabled={disabled}
          onClose={handleClose}
          onChange={handleChange}
          sx={{
            ...sx,
            width: '100%',
            '--csdIconColor': hasSelection ? selectedText : 'rgba(0,0,0,0.54)',

            '&.MuiOutlinedInput-root': {
              position: 'relative',
              mb: 0,
              ...(hasSelection ? { backgroundColor: selectedBg } : {}),

              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                right: 38,
                height: '60%',
                width: '1px',
                backgroundColor: 'var(--csdIconColor)',
                pointerEvents: 'none'
              }
            },

            '& .MuiOutlinedInput-notchedOutline': {
              ...(hasSelection ? { borderColor: selectedBg } : {})
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              ...(hasSelection ? { borderColor: selectedBg } : {})
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              ...(hasSelection ? { borderColor: selectedBg } : {})
            },

            '& .MuiSelect-icon': {
              color: 'var(--csdIconColor)'
            },

            '& .MuiSelect-select': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textTransform: 'uppercase',
              color: hasSelection ? selectedText : undefined,
              pr: '52px',
              py: 1.5
            }
          }}
          renderValue={(selectedValue) => {
            if (!selectedValue) return <span>SELECT</span>
            const numericValue = parseInt(selectedValue, 10)
            const opt = resolvedOptions.find((o) => o.value === numericValue)
            return <span>{opt?.label?.toUpperCase() ?? ''}</span>
          }}
          MenuProps={{
            ...(top
              ? {
                  anchorOrigin: { vertical: 'top', horizontal: 'left' },
                  transformOrigin: { vertical: 'bottom', horizontal: 'left' }
                }
              : {}),
            PaperProps: {
              sx: {
                width: paperWidth,
                maxWidth: 'none'
              }
            },
            MenuListProps: { sx: { py: 0 } }
          }}
        >
          {resolvedOptions.map((option) => {
            const isSelected = String(option.value) === selectValue
            const isDisabled = disabledStatuses.includes(option.value)
            const tooltip = statusTooltips[option.value] || ''

            const menuItem = (
              <MUIMenuItem
                key={option.value}
                value={String(option.value)}
                disabled={isDisabled}
                sx={{
                  justifyContent: 'flex-start',
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    pointerEvents: 'auto',
                    cursor: 'not-allowed'
                  }
                }}
                onClick={(e) => {
                  if (isDisabled) {
                    e.preventDefault()
                    e.stopPropagation()
                    return
                  }
                  if (isSelected) {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange(null)
                    setOpen(false)
                  }
                }}
              >
                <Pill
                  label={option.label.toUpperCase()}
                  color={option.color!}
                  backgroundColor={option.backgroundColor!}
                  sx={{
                    height: '25px',
                    pointerEvents: 'none',
                    [`& span`]: { fontSize: '0.75rem' }
                  }}
                />
              </MUIMenuItem>
            )

            if (isDisabled && tooltip) {
              return (
                <MUITooltip key={option.value} title={tooltip} arrow placement="left">
                  <span>{menuItem}</span>
                </MUITooltip>
              )
            }

            return menuItem
          })}
        </MUISelect>
      </div>
    </MUIStack>
  )
}
